import { createRef, JSXElementConstructor, PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import { debounce, isNil, noop, isUndefined } from 'lodash';
import { get, is, isImmutable, fromJS } from 'immutable';

// COMPONENTS
import MenuContent from './DropdownMenu.MenuContent.subcomponent';

// CONSTANS
import { KEY_CODES } from 'constants/common';

// STYLES
import {
  Container,
  Icon,
  DropdownContent,
  DropdownInput,
  DropdownButton,
  SubtleDropdownButton,
  CLASSES,
} from './DropdownMenu.styles';
import { ifInTestingMode } from 'utils/tests.utils';

const Dropdown = (props) => {
  const Component = props.buttonComponent || DropdownContent;
  return (
    <Component
      disabled={props.disabled}
      onFocus={props.onToggleMenu}
      onClick={props.onToggleMenu}
      onBlur={props.onBlurClose}
      onKeyDown={props.onKeyDown}
      readOnly={props.readOnly}
      iconOnly={props.iconOnly}
      icon={props.icon}
    >
      {props.buttonChildren !== undefined ? (
        props.buttonChildren
      ) : (
        <>
          <div className="display-text">{props.displayText}</div>
          <Icon className={getIcon(props.active)} />
        </>
      )}
    </Component>
  );
};

const InputDropdown = (props) => (
  <DropdownInput
    autoComplete="off"
    disabled={props.disabled}
    onFocus={props.onToggleMenu}
    onBlur={props.onBlurClose}
    name="filter"
    placeholder={props.displayText}
    value={props.query}
    onChange={props.onChangeQuery}
    onKeyDown={props.onKeyDown}
    readOnly={props.readOnly}
  />
);

InputDropdown.defaultProps = {
  query: '',
};

const ButtonDropdown = (props) => {
  const Wrapper = props.subtle ? SubtleDropdownButton : DropdownButton;

  const editable = !props.readOnly;

  return (
    <Wrapper
      disabled={props.disabled}
      onFocus={editable ? props.onToggleMenu : noop}
      onClick={editable ? props.onToggleMenu : noop}
      onBlur={editable ? props.onBlurClose : noop}
      // @ts-expect-error TS2769: No overload matches this call.
      hasPlaceholder={props.hasPlaceholder}
      hasSelection={props.hasSelection}
      onKeyDown={editable ? props.onKeyDown : noop}
      icon={props.textIcon}
      $readOnly={props.readOnly}
    >
      <div className="display-text">{props.displayText}</div>
      {editable && <Icon className={getIcon(props.active)} />}
    </Wrapper>
  );
};

function getIcon(active) {
  return active ? 'icon-caret-up-light' : 'icon-caret-down-light';
}

/**
 * @deprecated Please use `Select` from `components/Select` instead.
 */
class DropdownMenu extends PureComponent<{
  iconOnly?: boolean;
  icon?: React.ReactNode;
  buttonComponent?: JSXElementConstructor<object>;
  buttonChildren?: React.ReactNode;
}> {
  static getFilteredItems = (query: string, items, intl) => {
    let filteredItems = items;
    const lowerCaseQuery = query.toLowerCase();

    if (query.length > 0) {
      filteredItems = filteredItems.filter((item) => {
        const renderedValue = item.get('renderedValue');
        const value = item.get('renderedValue');

        if (isNil(renderedValue)) {
          if (isNil(value)) {
            // No way to filter if there's no value AND no rendered value
            return false;
          }

          return String(value).includes(lowerCaseQuery);
        }

        if (typeof renderedValue === 'string') {
          return renderedValue.includes(lowerCaseQuery);
        }

        const id = get(renderedValue, 'translationId');
        const values = get(renderedValue, 'values', {});

        return intl
          .formatMessage({
            id,
            values,
          })
          .toLowerCase()
          .includes(lowerCaseQuery);
      });
    }

    return filteredItems;
  };

  static getItemByValue = (items, initialValue) => items.findEntry((item) => item.get('value') === initialValue);

  static itemSelectHandler = (item, index, props, meta, event) => {
    if (item.get('disabled')) {
      return false;
    }
    const handleClick = item.get('handleClick', noop);
    const mutableItem = isImmutable(item) ? item.toJS() : item;

    handleClick(mutableItem, event);

    props.changeSelectionHandler(mutableItem, meta, props.name);

    return true;
  };

  static defaultProps = {
    button: false,
    changeSelectionHandler: () => undefined,
    className: '',
    ContentWrapper: undefined,
    disabled: false,
    defaultValue: undefined,
    input: false,
    maxItems: 5,
    placeholder: false,
    renderedValue: undefined,
    showSelected: undefined,
    initialValue: undefined,
    subtle: false,
    showIcon: true,
    icon: null,
    iconOnly: false,
  };

  static maybeUseDefault(entry, props) {
    const meta = { fromDefault: false };
    let selectedEntry = entry;

    if (isNil(selectedEntry) && !isNil(props.defaultValue)) {
      selectedEntry = props.items.findEntry((item) => item.get('value') === props.defaultValue);

      meta.fromDefault = true;
    }

    return [selectedEntry, meta];
  }

  constructor(props) {
    super(props);

    this.containerElement = createRef();

    let meta = {};
    let selected;
    let selectedEntry;
    let selectedIndex;

    // @ts-expect-error TS(2339): Property 'initialValue' does not exist on type 'Re... Remove this comment to see the full error message
    if (!isUndefined(this.props.initialValue)) {
      // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
      selectedEntry = DropdownMenu.getItemByValue(this.props.items, this.props.initialValue);
    }

    [selectedEntry, meta] = DropdownMenu.maybeUseDefault(selectedEntry, this.props);

    if (selectedEntry) {
      [selectedIndex, selected] = selectedEntry;
      // If we're swiching to the default value, we want to propogate it back
      // @ts-expect-error TS(2339): Property 'fromDefault' does not exist on type '{}'... Remove this comment to see the full error message
      if (meta.fromDefault) {
        DropdownMenu.itemSelectHandler(selected, selectedIndex, props, meta, {});
      }
    }

    this.state = {
      active: false,
      // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
      filteredItems: this.props.items,
      highlightedIndex: selectedEntry ? selectedEntry[0] : null,
      query: '',
      selected,
    };
  }

  componentDidUpdate(prevProps) {
    // @ts-expect-error TS(2339): Property 'initialValue' does not exist on type 'Re... Remove this comment to see the full error message
    if (!is(this.props.initialValue, prevProps.initialValue) || !is(this.props.items, prevProps.items)) {
      let meta = {
        fromDefault: false,
      };
      let selected;
      let selectedEntry;
      let selectedIndex;

      // @ts-expect-error TS(2339): Property 'initialValue' does not exist on type 'Re... Remove this comment to see the full error message
      if (!isUndefined(this.props.initialValue)) {
        // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
        selectedEntry = DropdownMenu.getItemByValue(this.props.items, this.props.initialValue);
      }

      [selectedEntry, meta] = DropdownMenu.maybeUseDefault(selectedEntry, this.props);

      // @ts-expect-error TS(2339): Property 'selected' does not exist on type 'Readon... Remove this comment to see the full error message
      if (selectedEntry && selectedEntry !== this.state.selected) {
        [selectedIndex, selected] = selectedEntry;

        if (meta.fromDefault) {
          DropdownMenu.itemSelectHandler(selected, selectedIndex, this.props, meta, {});
        }
      }

      this.setState({
        // @ts-expect-error TS(2339): Property 'query' does not exist on type 'Readonly<... Remove this comment to see the full error message
        filteredItems: DropdownMenu.getFilteredItems(this.state.query, this.props.items, this.props.intl),
        selected,
      });

      // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
      if (!is(prevProps.items, this.props.items)) {
        this.setFilteredItems();
      }
    }
  }

  containerElement;

  setFilteredItems = () => {
    this.setState({
      // @ts-expect-error TS(2339): Property 'query' does not exist on type 'Readonly<... Remove this comment to see the full error message
      filteredItems: DropdownMenu.getFilteredItems(this.state.query, this.props.items, this.props.intl),
    });
  };

  boundingRect = null;

  handleToggleDropdown = debounce(
    () => {
      // @ts-expect-error TS(2339): Property 'active' does not exist on type 'Readonly... Remove this comment to see the full error message
      if (!this.state.active) {
        this.showDropdown();
        // @ts-expect-error TS(2339): Property 'onClick' does not exist on type 'Readonl... Remove this comment to see the full error message
        if (typeof this.props.onClick === 'function') {
          // @ts-expect-error TS(2339): Property 'onClick' does not exist on type 'Readonl... Remove this comment to see the full error message
          this.props.onClick();
        }
      } else {
        this.hideDropdown();
      }
    },
    150,
    { leading: true, trailing: false }
  );

  showDropdown = () => {
    this.setState({ active: true });
  };

  hideDropdown = () => {
    this.setState({ active: false });
  };

  handleOnBlur = () => {
    this.setState({
      active: false,
      query: '',
    });
  };

  handleChangeQuery = (event) => {
    const query = event.currentTarget.value;
    // @ts-expect-error TS(2339): Property 'getFilteredItems' does not exist on type... Remove this comment to see the full error message
    const filteredItems = this.constructor.getFilteredItems(query, this.props.items, this.props.intl);
    this.setState({ query, filteredItems });
  };

  getItemClickHandler = (item, index, event) => {
    const newState = { ...this.state, active: false };

    if (DropdownMenu.itemSelectHandler(item, index, this.props, {}, event)) {
      // @ts-expect-error TS(2339): Property 'selected' does not exist on type '{ acti... Remove this comment to see the full error message
      newState.selected = item;
      // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
      newState.highlightedIndex = index;
    }

    this.setState(newState);
  };

  // @ts-expect-error TS(2339): Property 'filteredItems' does not exist on type 'R... Remove this comment to see the full error message
  getFocusedItem = (focusedIndex) => (focusedIndex >= 0 ? this.state.filteredItems.get(this.props.focusedIndex) : null);

  getRenderSelected = () => {
    // If "showSelected" is not explicity false then render the selected value,
    // UNLESS "button" is true, in which case only render the selected value
    // selected if "showSelected" is explicity true.
    // @ts-expect-error TS(2339): Property 'button' does not exist on type 'Readonly... Remove this comment to see the full error message
    const { button, showSelected } = this.props;

    if (showSelected === false) {
      return false;
    }

    if (button === true && showSelected !== true) {
      return false;
    }

    return true;
  };

  /**
   * TODO: A lot of this rendering logic is shared with
   * InfoField, it would be great to find a way consolidate it.
   */
  getDisplayText = () => {
    // @ts-expect-error TS(2339): Property 'intl' does not exist on type 'Readonly<{... Remove this comment to see the full error message
    const { intl, renderedValue, placeholder } = this.props;
    // @ts-expect-error TS(2339): Property 'selected' does not exist on type 'Readon... Remove this comment to see the full error message
    const { selected } = this.state;

    let displayText;

    if (selected && this.getRenderSelected()) {
      const selectedRenderedValue = selected.get('renderedValue');

      if (!isNil(selectedRenderedValue)) {
        // If the selected item has no translation, but has a
        // rendered value, use that.
        displayText =
          typeof selectedRenderedValue === 'object'
            ? intl.formatMessage({
                id: selectedRenderedValue.get('translationId'),
              })
            : selected.get('renderedValue');
      } else {
        // If the selected item has no translation and no rendered
        // value, just display the value.
        displayText = selected.get('value');
      }
    } else if (!isNil(renderedValue)) {
      // If there is no selection, but there is a rendered value, use that.
      const id = get(renderedValue, 'translationId');
      const values = get(renderedValue, 'values', {});

      displayText = typeof renderedValue === 'object' ? intl.formatMessage({ id }, values) : renderedValue;
    } else if (!isNil(placeholder) && typeof placeholder !== 'boolean') {
      displayText =
        typeof placeholder === 'object' ? intl.formatMessage({ id: placeholder.translationId }) : placeholder;
    }

    if (typeof displayText === 'boolean') {
      displayText = displayText ? intl.formatMessage({ id: 'true' }) : intl.formatMessage({ id: 'false' });
    }

    return isNil(displayText) ? '' : String(displayText);
  };

  handleKeyDown = (event) => {
    // @ts-expect-error TS(2339): Property 'active' does not exist on type 'Readonly... Remove this comment to see the full error message
    if (!this.state.active) {
      return;
    }

    // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
    if (!this.props.items.size) {
      return;
    }

    switch (event.keyCode) {
      case KEY_CODES.ENTER: {
        event.preventDefault();

        // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
        const selectedItem = this.props.items.get(this.state.highlightedIndex);

        if (selectedItem) {
          // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
          this.getItemClickHandler(selectedItem, this.state.highlightedIndex);
        }

        break;
      }
      case KEY_CODES.DOWN:
        event.preventDefault();
        // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
        if (this.state.highlightedIndex === null) {
          this.setState({
            highlightedIndex: 0,
          });
          break;
        }
        // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
        if (this.state.highlightedIndex + 1 < this.props.items.size) {
          this.setState({
            // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
            highlightedIndex: this.state.highlightedIndex + 1,
          });
        }
        break;
      case KEY_CODES.UP:
        event.preventDefault();
        // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
        if (this.state.highlightedIndex > 0) {
          this.setState({
            // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
            highlightedIndex: this.state.highlightedIndex - 1,
          });
        }
        break;
      case KEY_CODES.ESC:
        event.preventDefault();
        this.handleToggleDropdown();
        break;
      default:
    }
  };

  render() {
    // @ts-expect-error TS(2339): Property 'items' does not exist on type 'Readonly<... Remove this comment to see the full error message
    const { items, placeholder, renderedValue, subtle, showIcon } = this.props;
    // @ts-expect-error TS(2339): Property 'selected' does not exist on type 'Readon... Remove this comment to see the full error message
    const { selected } = this.state;
    const displayText = this.getDisplayText();
    // @ts-expect-error TS(2339): Property 'disabled' does not exist on type 'Readon... Remove this comment to see the full error message
    const disabled = !items.size || this.props.disabled;

    // @ts-expect-error TS(2339): Property 'className' does not exist on type 'Reado... Remove this comment to see the full error message
    let { className } = this.props;

    if (placeholder === false || !isNil(renderedValue) || selected) {
      className = `${className} ${CLASSES.HAS_SELECTION}`;
    }

    // @ts-expect-error TS(2339): Property 'filteredItems' does not exist on type 'R... Remove this comment to see the full error message
    const icon = this.state.filteredItems
      .filter((item) => is(item.get('renderedValue'), fromJS(renderedValue)))
      .getIn([0, 'icon'])
      ?.toJS();

    return (
      <Container ref={this.containerElement} className={className} data-testid={ifInTestingMode('dropdown-menu')}>
        {/* @ts-expect-error TS(2339): Property 'input' does not exist on type 'Readonly<... Remove this comment to see the full error message */}
        {!this.props.input && !this.props.button && (
          <Dropdown
            displayText={displayText}
            // @ts-expect-error TS(2339): Property 'active' does not exist on type 'Readonly... Remove this comment to see the full error message
            active={this.state.active}
            onToggleMenu={this.handleToggleDropdown}
            onBlurClose={this.handleOnBlur}
            onKeyDown={this.handleKeyDown}
            disabled={disabled}
            subtle={subtle}
            // @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message
            readOnly={this.props.readOnly}
            iconOnly={this.props.iconOnly}
            icon={this.props.icon}
            buttonComponent={this.props.buttonComponent}
            buttonChildren={this.props.buttonChildren}
          />
        )}

        {/* @ts-expect-error TS(2339): Property 'input' does not exist on type 'Readonly<... Remove this comment to see the full error message */}
        {this.props.input && (
          <InputDropdown
            // @ts-expect-error TS(2339): Property 'active' does not exist on type 'Readonly... Remove this comment to see the full error message
            active={this.state.active}
            displayText={displayText}
            // @ts-expect-error TS(2339): Property 'query' does not exist on type 'Readonly<... Remove this comment to see the full error message
            query={this.state.query}
            onToggleMenu={this.handleToggleDropdown}
            onChangeQuery={this.handleChangeQuery}
            onBlurClose={this.handleOnBlur}
            onKeyDown={this.handleKeyDown}
            // @ts-expect-error TS(2339): Property 'intl' does not exist on type 'Readonly<{... Remove this comment to see the full error message
            intl={this.props.intl}
            disabled={disabled}
            // @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message
            readOnly={this.props.readOnly}
          />
        )}

        {/* @ts-expect-error TS(2339): Property 'button' does not exist on type 'Readonly... Remove this comment to see the full error message */}
        {this.props.button && (
          <ButtonDropdown
            // @ts-expect-error TS(2339): Property 'active' does not exist on type 'Readonly... Remove this comment to see the full error message
            active={this.state.active}
            displayText={displayText}
            textIcon={showIcon ? icon : undefined}
            onToggleMenu={this.handleToggleDropdown}
            onBlurClose={this.handleOnBlur}
            // @ts-expect-error TS(2339): Property 'placeholder' does not exist on type 'Rea... Remove this comment to see the full error message
            hasPlaceholder={Boolean(this.props.placeholder)}
            // @ts-expect-error TS(2339): Property 'selected' does not exist on type 'Readon... Remove this comment to see the full error message
            hasSelection={!isNil(this.state.selected)}
            onKeyDown={this.handleKeyDown}
            disabled={disabled}
            // @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message
            readOnly={this.props.readOnly}
            subtle={subtle}
          />
        )}

        {this.containerElement.current && (
          <MenuContent
            // @ts-expect-error TS(2322): Type '{ active: any; items: any; containerElement:... Remove this comment to see the full error message
            active={this.state.active}
            // @ts-expect-error TS(2339): Property 'filteredItems' does not exist on type 'R... Remove this comment to see the full error message
            items={this.state.filteredItems}
            containerElement={this.containerElement.current}
            // @ts-expect-error TS(2339): Property 'ContentWrapper' does not exist on type '... Remove this comment to see the full error message
            ContentWrapper={this.props.ContentWrapper}
            // @ts-expect-error TS(2339): Property 'maxItems' does not exist on type 'Readon... Remove this comment to see the full error message
            maxItems={this.props.maxItems}
            // @ts-expect-error TS(2339): Property 'query' does not exist on type 'Readonly<... Remove this comment to see the full error message
            query={this.state.query}
            // @ts-expect-error TS(2339): Property 'highlightedIndex' does not exist on type... Remove this comment to see the full error message
            focusedIndex={this.state.highlightedIndex}
            getItemClickHandler={this.getItemClickHandler}
            onOutsideClick={this.handleOnBlur}
            // @ts-expect-error TS(2339): Property 'className' does not exist on type 'Reado... Remove this comment to see the full error message
            className={this.props.className}
            // @ts-expect-error TS(2339): Property 'readOnly' does not exist on type 'Readon... Remove this comment to see the full error message
            readOnly={this.props.readOnly}
            showIcon={showIcon}
          />
        )}
      </Container>
    );
  }
}

export default injectIntl(DropdownMenu);
